import camelcaseKeys from 'camelcase-keys';
import AudiencesAPI from 'dashboard/api/audiences';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

// `criteria` (and the contact payload inside a preview response) travel the
// wire in the snake_case shape the filter engine expects (attribute_key,
// filter_operator, ...); camelCasing them would have to be undone again
// before sending them back, so both are kept out of the conversion.
const camelizeAudience = data =>
  camelcaseKeys(data || {}, { deep: true, stopPaths: ['criteria'] });

const camelizeContact = data =>
  camelcaseKeys(data || {}, {
    deep: true,
    stopPaths: ['custom_attributes', 'additional_attributes'],
  });

export const useAudiencesStore = createStore({
  name: 'audiences',
  type: 'pinia',
  API: AudiencesAPI,
  state: () => ({
    previewResult: {
      count: 0,
      currentPage: 1,
      withoutPhoneCount: 0,
      contacts: [],
    },
  }),

  getters: {
    getAudiencesList: state => state.records,
    getPreviewResult: state => state.previewResult,
  },

  actions: () => ({
    upsertAudienceRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get() {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await AudiencesAPI.get();
        this.records = camelizeAudience(data);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await AudiencesAPI.show(id);
        const audience = camelizeAudience(data);
        this.upsertAudienceRecord(audience);
        return audience;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async create({ name, criteria }) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await AudiencesAPI.create({
          audience: { name, criteria },
        });
        const audience = camelizeAudience(data);
        this.records.push(audience);
        return audience;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, name, criteria }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await AudiencesAPI.update(id, {
          audience: { name, criteria },
        });
        const audience = camelizeAudience(data);
        this.upsertAudienceRecord(audience);
        return audience;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await AudiencesAPI.delete(id);
        this.records = this.records.filter(record => record.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async fetchContacts({ id, page = 1 }) {
      this.setUIFlag({ fetchingContacts: true });
      try {
        const { data } = await AudiencesAPI.getContacts(id, page);
        return {
          count: data.meta?.count || 0,
          currentPage: Number(data.meta?.current_page) || 1,
          contacts: camelizeContact(data.payload || []),
        };
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingContacts: false });
      }
    },

    async preview(criteria) {
      this.setUIFlag({ previewingAudience: true });
      try {
        const { data } = await AudiencesAPI.preview(criteria);
        this.previewResult = {
          count: data.meta?.count || 0,
          currentPage: data.meta?.current_page || 1,
          withoutPhoneCount: data.meta?.without_phone_count || 0,
          contacts: camelizeContact(data.payload || []),
        };
        return this.previewResult;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ previewingAudience: false });
      }
    },
  }),
});
